import React, { useRef, useState } from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowUpRight,
  FiCode,
  FiCpu,
  FiShield,
  FiCloud,
  FiGitBranch,
  FiLayers,
  FiCompass,
  FiPenTool,
  FiTrendingUp,
  FiRefreshCw,
} from "react-icons/fi"
import ButtonLink from "../components/ButtonLink"
import Link from "../components/Link"
import PageMetadata from "../components/PageMetadata"
import { translateMessageId } from "../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.8rem, 3vw, 2.6rem);
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.25rem;
    line-height: 1.35;
    margin: 0 0 1rem;
  }
  a:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Eyebrow = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 100%;
  background: #08121c;
  color: #fff;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
        90deg,
        rgba(4, 12, 21, 0.8) 0%,
        rgba(4, 12, 21, 0.62) 42%,
        rgba(4, 12, 21, 0.08) 80%
      ),
      linear-gradient(0deg, rgba(4, 12, 21, 0.45), transparent 45%);
    pointer-events: none;
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 700px) {
    &::after {
      background: linear-gradient(
        90deg,
        rgba(4, 12, 21, 0.9),
        rgba(4, 12, 21, 0.64)
      );
    }
  }
`
const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
`
const HeroContent = styled(Container)`
  min-height: 610px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-top: 4.5rem;
  padding-bottom: 4.5rem;
  h1 {
    max-width: 760px;
    color: #fff;
    font-size: clamp(2.75rem, 5.5vw, 4.75rem);
    font-weight: 500;
    line-height: 1.06;
    letter-spacing: -0.045em;
    margin: 0 0 1.75rem;
    text-wrap: balance;
  }
  > p {
    max-width: 560px;
    color: #e0e6ec;
    font-size: 1.125rem;
    line-height: 1.7;
    margin: 0 0 2rem;
  }
  @media (max-width: 700px) {
    min-height: 600px;
    padding-top: 3rem;
    padding-bottom: 3rem;
    h1 {
      font-size: clamp(2.5rem, 8.8vw, 3.75rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`
const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 1.75rem;
  &::before {
    content: "";
    width: 2rem;
    height: 2px;
    flex-shrink: 0;
    background: #6d9dff;
  }
`
const HeroActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  @media (max-width: 400px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 1.4rem;
  }
`
const HeroPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  background: #fff;
  color: #102030 !important;
  padding: 1rem 1.25rem;
  border: 1px solid #fff;
  border-radius: 0;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #dce7ff;
    border-color: #dce7ff;
  }
  svg {
    flex-shrink: 0;
  }
`
const HeroSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  &:hover {
    border-color: #fff;
  }
`
const Lead = styled.p`
  max-width: 62ch;
  font-size: 1.125rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text200};
  margin: 0 0 1.75rem;
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  a {
    padding: 0.8rem 1.1rem;
    border-radius: 6px;
    white-space: normal;
    font-weight: 600;
  }
  @media (max-width: 400px) {
    flex-direction: column;
  }
`
const Note = styled.p`
  margin: 1.25rem 0 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text200};
`
const ServiceRail = styled.nav`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 800px) {
    ${Container} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 420px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
  }
`
const ServiceRailLink = styled(Link)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.7rem;
  padding: 1.5rem;
  border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  &:last-child {
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  > span {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.7rem;
    letter-spacing: 0.06em;
  }
  > strong {
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    font-size: 0.95rem;
  }
  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
  @media (max-width: 800px) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  @media (max-width: 420px) {
    padding: 1rem 0;
    border-left: 0;
    &:last-child {
      border-right: 0;
      border-bottom: 0;
    }
  }
`
const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const SoftSection = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const ServicesHeader = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  align-items: end;
  gap: 3rem;
  margin-bottom: 2.5rem;
  h2 {
    font-size: clamp(2rem, 3.5vw, 3.2rem);
    font-weight: 500;
    max-width: 19ch;
    margin-bottom: 0;
    text-wrap: balance;
  }
  ${Lead} {
    font-size: 1.05rem;
    margin-bottom: 0.2rem;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`
const ServiceExplorer = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.7fr);
  gap: 1.5rem;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`
const ServiceTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  @media (max-width: 800px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`
const ServiceTab = styled.button`
  flex: 1;
  display: grid;
  grid-template-columns: 2.6rem minmax(0, 1fr) 1rem;
  gap: 0.85rem;
  align-items: center;
  width: 100%;
  padding: 1.5rem 1.1rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  > span:first-child {
    display: grid;
    place-items: center;
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.ednBackground};
    color: ${({ theme }) => theme.colors.primary};
    svg {
      width: 21px;
      height: 21px;
    }
  }
  strong {
    display: block;
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1.35;
  }
  small {
    display: block;
    margin-top: 0.5rem;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.8rem;
    line-height: 1.5;
  }
  > svg {
    color: ${({ theme }) => theme.colors.primary};
    opacity: 0.4;
  }
  &[aria-selected="true"] {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.ednBackground};
    box-shadow: inset 3px 0 ${({ theme }) => theme.colors.primary};
    > span:first-child {
      background: #6f91e91a;
    }
    > svg {
      opacity: 1;
    }
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: no-preference) {
    transition: background 180ms ease, border-color 180ms ease;
  }
  @media (max-width: 800px) {
    padding: 1.1rem;
    grid-template-columns: 2.6rem minmax(0, 1fr);
    > svg {
      display: none;
    }
  }
  @media (max-width: 480px) {
    padding: 1rem;
    grid-template-columns: 1fr;
    gap: 0.65rem;
    strong {
      font-size: 0.95rem;
    }
    small {
      display: none;
    }
  }
`
const ServicePanel = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 570px;
  overflow: hidden;
  border-radius: 10px;
  background: #091722;
  color: #fff;
  &[hidden] {
    display: none;
  }
  &::after {
    content: "";
    position: absolute;
    z-index: -1;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      180deg,
      #06121a08 0%,
      #07141d30 24%,
      #091722e8 56%,
      #091722 82%
    );
  }
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 4px;
  }
  @media (max-width: 480px) {
    &::after {
      background: linear-gradient(
        180deg,
        #06121a08 0px,
        #07141d30 130px,
        #091722d9 230px,
        #091722 380px
      );
    }
  }
`
const ServiceImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -2;
`
const ServiceMarker = styled.span`
  position: absolute;
  top: 1.5rem;
  left: 1.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.5rem 0.8rem;
  border: 1px solid #ffffff30;
  border-radius: 4px;
  background: #091722bf;
  color: #fff;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $accent }) => $accent};
  }
`
const ServiceContent = styled.div`
  padding: 230px 2.25rem 2.25rem;
  h3 {
    color: #fff;
    font-size: clamp(1.8rem, 2.7vw, 2.5rem);
    font-weight: 500;
    letter-spacing: -0.025em;
    line-height: 1.15;
    max-width: 21ch;
    margin-bottom: 1rem;
    text-wrap: balance;
  }
  p {
    max-width: 52ch;
    font-size: 1rem;
    line-height: 1.65;
    color: #d2dde5;
    margin-bottom: 1.25rem;
  }
  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    list-style: none;
    padding: 0;
    margin: 0 0 1.75rem;
    li {
      margin: 0;
      padding: 0.3rem 0.65rem;
      border: 1px solid #ffffff30;
      border-radius: 4px;
      color: #d2dde5;
      font-size: 0.75rem;
      line-height: 1.5;
      &::before {
        content: none;
      }
    }
  }
  @media (max-width: 480px) {
    padding: 200px 1.5rem 1.5rem;
  }
`
const ServiceAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  min-height: 48px;
  padding: 0.85rem 1.1rem;
  border-radius: 3px;
  background: #fff;
  color: #091722;
  font-size: 0.9rem;
  line-height: 1.4;
  font-weight: 600;
  text-decoration: none;
  svg {
    flex-shrink: 0;
  }
  &:hover {
    color: #091722;
    background: #e6eef7;
    text-decoration: none;
    opacity: 1;
    svg {
      fill: none;
      transform: none;
    }
  }
  &:focus-visible {
    outline-color: #fff;
  }
`
const TextLink = styled(Link)`
  display: inline-flex;
  gap: 0.75rem;
  align-items: center;
  font-weight: 600;
  text-decoration: none;
  margin-top: auto;
  font-size: 0.95rem;
  svg {
    flex-shrink: 0;
  }
  &:hover {
    text-decoration: underline;
  }
`
const ExtraServices = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.75rem 1.5rem;
  padding-top: 2rem;
  font-size: 0.875rem;
  span {
    color: ${({ theme }) => theme.colors.text200};
  }
`
const ProductSection = styled(Section)`
  position: relative;
  background: ${({ theme }) => theme.colors.ednBackground};
  background-image: radial-gradient(
    ellipse at 100% 0%,
    #6494e912,
    transparent 55%
  );
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
  a:hover svg {
    fill: none;
  }
`
const ProductHeader = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 3rem;
  align-items: end;
  margin-bottom: 2.5rem;
  ${Eyebrow} {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }
  ${Eyebrow}::before {
    content: "";
    width: 1.75rem;
    height: 2px;
    background: currentColor;
  }
  h2 {
    font-size: clamp(2rem, 3.5vw, 3.2rem);
    font-weight: 500;
    margin: 0;
    max-width: 18ch;
    text-wrap: balance;
  }
  ${Lead} {
    font-size: 1rem;
    max-width: 46ch;
    margin-bottom: 1.25rem;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`
const CatalogLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.text200};
  padding: 0.65rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    border-color: currentColor;
  }
  svg {
    flex-shrink: 0;
  }
`
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 1.25rem;
  @media (max-width: 1000px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`
const ProductTile = styled.article`
  --product-accent: ${({ $accent }) => $accent};
  min-width: 0;
  grid-row: ${({ $featured }) => ($featured ? "span 2" : "auto")};
  @media (max-width: 1000px) {
    grid-row: auto;
    &:first-child {
      grid-column: 1 / -1;
    }
  }
  @media (max-width: 700px) {
    &:first-child {
      grid-column: auto;
    }
  }
`
const ProductVisual = styled.div`
  position: ${({ $featured }) => ($featured ? "absolute" : "relative")};
  inset: ${({ $featured }) => ($featured ? "0" : "auto")};
  overflow: hidden;
  min-width: 0;
  background: #132632;
  > .gatsby-image-wrapper {
    position: absolute !important;
    inset: 0;
    height: 100%;
    transition: transform 500ms ease;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: ${({ $featured }) =>
      $featured
        ? "linear-gradient(0deg, #071823 0%, #071823f5 20%, #071823af 43%, #07182305 76%), linear-gradient(180deg, #07182366, transparent 35%)"
        : "linear-gradient(180deg, #07182366, transparent 50%)"};
  }
  @media (max-width: 1000px) {
    min-height: ${({ $featured }) => ($featured ? "0" : "190px")};
  }
  @media (prefers-reduced-motion: reduce) {
    > .gatsby-image-wrapper {
      transition: none;
    }
  }
`
const ProductBadge = styled.div`
  position: absolute;
  z-index: 1;
  top: 1.25rem;
  left: 1.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  color: #fff;
  background: #081923d9;
  border: 1px solid #ffffff35;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 500;
  svg {
    color: var(--product-accent);
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`
const ProductBody = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  align-self: ${({ $featured }) => ($featured ? "end" : "stretch")};
  padding: ${({ $featured }) => ($featured ? "2.25rem" : "1.6rem")};
  h3 {
    color: ${({ $featured, theme }) =>
      $featured ? "#fff" : theme.colors.text};
    font-size: ${({ $featured }) =>
      $featured ? "clamp(2rem, 3.1vw, 2.7rem)" : "1.5rem"};
    font-weight: 500;
    line-height: 1.12;
    letter-spacing: -0.035em;
    margin: 0 0 1rem;
  }
  > p {
    color: ${({ $featured, theme }) =>
      $featured ? "#d1dfe8" : theme.colors.text200};
    font-size: ${({ $featured }) => ($featured ? "1rem" : "0.9rem")};
    line-height: 1.7;
    margin: 0 0 1.5rem;
    max-width: 40ch;
  }
  @media (max-width: 1000px) {
    padding: 1.75rem;
  }
  @media (max-width: 420px) {
    padding: 1.5rem;
  }
`
const ProductCategory = styled.div`
  color: ${({ $featured, theme }) =>
    $featured ? "#b0eddc" : theme.colors.text200};
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.6;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  margin-bottom: 0.8rem;
`
const ProductTags = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  li {
    color: #bddbd7;
    background: #b0eddc0c;
    border: 1px solid #b0eddc33;
    border-radius: 3px;
    padding: 0.35rem 0.6rem;
    font-size: 0.7rem;
    margin: 0;
  }
  li::before {
    display: none;
  }
`
const ProductAction = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid
    ${({ $featured, theme }) =>
      $featured ? "#ffffff30" : theme.colors.lightBorder};
  color: ${({ $featured, theme }) => ($featured ? "#fff" : theme.colors.text)};
  font-size: 0.85rem;
  font-weight: 600;
  > svg {
    width: 1.2rem;
    height: 1.2rem;
    flex-shrink: 0;
    transition: transform 200ms ease;
  }
  @media (prefers-reduced-motion: reduce) {
    > svg {
      transition: none;
    }
  }
`
const ProductEntry = styled(Link)`
  display: grid;
  grid-template-columns: ${({ $featured }) =>
    $featured ? "1fr" : "0.38fr 0.62fr"};
  min-height: ${({ $featured }) => ($featured ? "560px" : "270px")};
  height: 100%;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: ${({ $featured, theme }) =>
    $featured ? "#071823" : theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-bottom: 3px solid var(--product-accent);
  border-radius: 5px;
  text-decoration: none;
  color: inherit;
  transition: border-color 200ms ease, box-shadow 200ms ease;
  &:hover,
  &:focus-visible {
    text-decoration: none;
    opacity: 1;
    border-color: var(--product-accent);
    box-shadow: 0 12px 30px #07182312;
  }
  &:hover ${ProductAction}, &:focus-visible ${ProductAction} {
    text-decoration: underline;
    text-underline-offset: 4px;
  }
  &:hover ${ProductAction} > svg,
  &:focus-visible ${ProductAction} > svg {
    transform: translate(3px, -3px);
  }
  &:hover svg {
    fill: none;
  }
  @media (hover: hover) and (prefers-reduced-motion: no-preference) {
    &:hover ${ProductVisual} > .gatsby-image-wrapper {
      transform: scale(1.035);
    }
  }
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    min-height: ${({ $featured }) => ($featured ? "490px" : "0")};
  }
  @media (max-width: 700px) {
    min-height: ${({ $featured }) => ($featured ? "520px" : "0")};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
const MethodSection = styled(Section)`
  background: ${({ theme }) => theme.colors.background};
  background-image: radial-gradient(
    ellipse at 0% 0%,
    #7ca7ed0d,
    transparent 50%
  );
`
const MethodHeader = styled(ServicesHeader)`
  margin-bottom: 3rem;
  h2 {
    max-width: 21ch;
  }
`
const MethodJourney = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;
  list-style: none;
  margin: 0;
  padding: 0;
  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 2rem;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    row-gap: 1.25rem;
  }
`
const MethodConnector = styled.span`
  position: absolute;
  top: 27px;
  left: 66px;
  right: -0.5rem;
  height: 1px;
  background: ${({ theme }) => (theme.isDark ? "#ffffff24" : "#173b7226")};
  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: -3px;
    width: 7px;
    height: 7px;
    border-top: 1px solid
      ${({ theme }) => (theme.isDark ? "#ffffff50" : "#173b7250")};
    border-right: 1px solid
      ${({ theme }) => (theme.isDark ? "#ffffff50" : "#173b7250")};
    transform: rotate(45deg);
  }
`
const MethodStage = styled.li`
  --stage-color: ${({ $tone, theme }) =>
    theme.isDark ? $tone.light : $tone.dark};
  --stage-wash: ${({ $tone }) => $tone.wash};
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  padding: 0;
  &::before {
    content: none;
  }
  &:last-child ${MethodConnector} {
    display: none;
  }
  @media (min-width: 601px) and (max-width: 1000px) {
    &:nth-child(even) ${MethodConnector} {
      display: none;
    }
  }
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 0.85rem;
    ${MethodConnector} {
      top: 52px;
      bottom: -8px;
      left: 19px;
      right: auto;
      width: 1px;
      height: auto;
      &::after {
        top: auto;
        bottom: 0;
        right: -3px;
        transform: rotate(135deg);
      }
    }
  }
`
const MethodNumber = styled.span`
  position: relative;
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  margin-bottom: 1.25rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background};
  background-image: linear-gradient(var(--stage-wash), var(--stage-wash));
  color: var(--stage-color);
  font-size: 1.05rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    font-size: 0.85rem;
    margin-bottom: 0;
  }
`
const MethodCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-top: 2px solid var(--stage-color);
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  background-image: radial-gradient(
    ellipse at 100% 0%,
    var(--stage-wash),
    transparent 75%
  );
  box-shadow: 0 10px 30px #061a3205;
  h3 {
    font-size: 1.2rem;
    line-height: 1.35;
    min-height: 2.7em;
    margin-bottom: 0.85rem;
    letter-spacing: -0.015em;
  }
  > p {
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.65;
    font-size: 0.925rem;
  }
  && > p {
    margin: 0 0 1.5rem;
  }
  @media (max-width: 600px) {
    padding: 1.25rem;
    h3 {
      min-height: 0;
    }
  }
  @media (max-width: 360px) {
    padding: 1rem;
  }
`
const MethodPhase = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1.5rem;
  color: var(--stage-color);
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  svg {
    width: 21px;
    height: 21px;
    flex-shrink: 0;
  }
`
const MethodOutcome = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  span {
    display: block;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.7rem;
  }
  strong {
    display: block;
    color: ${({ theme }) => theme.colors.text};
    font-weight: 500;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`
const MethodFollowThrough = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  margin-top: 2rem;
  padding: 1.5rem 1.75rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.ednBackground};
  > div {
    display: flex;
    align-items: center;
    gap: 1rem;
    > svg {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  strong {
    font-size: 0.95rem;
    font-weight: 600;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.85rem;
    line-height: 1.6;
    margin: 0.35rem 0 0;
    max-width: 54ch;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
    min-height: 44px;
    padding: 0.6rem 0;
    font-size: 0.9rem;
    font-weight: 600;
    border-bottom: 1px solid currentColor;
    text-decoration: none;
    svg {
      flex-shrink: 0;
    }
    &:hover svg {
      fill: none;
    }
  }
  @media (max-width: 800px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
  }
`
const About = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`
const Contact = styled(Section)`
  text-align: center;
  ${Lead} {
    margin-left: auto;
    margin-right: auto;
  }
  ${Actions} {
    justify-content: center;
  }
`

const HomePage = ({ data }) => {
  const intl = useIntl()
  const [activeService, setActiveService] = useState(0)
  const serviceTabs = useRef([])
  const t = (key) => translateMessageId(`home-${key}`, intl)
  const methodSteps = [
    {
      icon: FiCompass,
      tone: { dark: "#2256a8", light: "#a7ccff", wash: "#659bff12" },
    },
    {
      icon: FiPenTool,
      tone: { dark: "#6553a8", light: "#c9baff", wash: "#aa93ff12" },
    },
    {
      icon: FiCode,
      tone: { dark: "#086a70", light: "#98dfdd", wash: "#7dd4d012" },
    },
    {
      icon: FiTrendingUp,
      tone: { dark: "#27704f", light: "#a6e0bd", wash: "#8edcab12" },
    },
  ]
  const email = `mailto:gerencia@newinntech.com?subject=${encodeURIComponent(
    t("email-subject")
  )}&body=${encodeURIComponent(t("email-body"))}`
  const solutions = [
    {
      key: "software",
      icon: FiCode,
      to: "/desarrollo/",
      image: data.serviceSoftware,
      accent: "#a7ccff",
    },
    {
      key: "ai",
      icon: FiCpu,
      to: "/ai-service/",
      image: data.serviceAi,
      accent: "#cbbcff",
    },
    {
      key: "cloud",
      icon: FiCloud,
      to: "/arquitectura/detalle/",
      image: data.serviceCloud,
      accent: "#9ce4ef",
    },
    {
      key: "devsecops",
      icon: FiShield,
      to: "/devsecops/",
      image: data.serviceDevsecops,
      accent: "#b0eddc",
    },
  ]
  const handleServiceKey = (event, index) => {
    let next = index
    if (event.key === "ArrowRight" || event.key === "ArrowDown")
      next = (index + 1) % solutions.length
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp")
      next = (index + solutions.length - 1) % solutions.length
    else if (event.key === "Home") next = 0
    else if (event.key === "End") next = solutions.length - 1
    else return
    event.preventDefault()
    setActiveService(next)
    serviceTabs.current[next].focus()
  }
  const products = [
    {
      key: "devops",
      name: "DevOps Monitor",
      to: "/productos/devops-monitor/",
      icon: FiGitBranch,
      image: data.productDevops,
      accent: "#b0eddc",
      position: "42% center",
    },
    {
      key: "pmo",
      name: "PMO Manager",
      to: "/productos/pmo-manager/",
      icon: FiLayers,
      image: data.productPmo,
      accent: "#a7ccff",
      position: "53% center",
    },
    {
      key: "ai",
      name: "Gencode AI",
      to: "/productos/gencode-ai/",
      icon: FiCode,
      image: data.productGencode,
      accent: "#cbbcff",
      position: "32% center",
    },
  ]
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
      />
      <Hero>
        <HeroImage
          fluid={{
            ...data.corporateHero.childImageSharp.fluid,
            sizes: "(max-width: 700px) 1300px, 100vw",
          }}
          alt=""
          loading="eager"
          fadeIn={false}
          imgStyle={{ objectPosition: "65% center" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>{t("headline")}</h1>
          <p>{t("intro")}</p>
          <HeroActions>
            <HeroPrimary to="#contacto">
              {t("contact-cta")}
              <FiArrowRight aria-hidden="true" />
            </HeroPrimary>
            <HeroSecondary to="#soluciones">
              {t("solutions-cta")}
              <FiArrowRight aria-hidden="true" />
            </HeroSecondary>
          </HeroActions>
        </HeroContent>
      </Hero>
      <ServiceRail aria-label={t("capabilities-label")}>
        <Container>
          {solutions.map(({ key, to }, index) => (
            <ServiceRailLink key={key} to={to}>
              <span aria-hidden="true">0{index + 1}</span>
              <strong>
                {t(`${key}-title`)}
                <FiArrowRight aria-hidden="true" />
              </strong>
            </ServiceRailLink>
          ))}
        </Container>
      </ServiceRail>
      <Section id="soluciones" aria-labelledby="solutions-title">
        <Container>
          <ServicesHeader>
            <div>
              <Eyebrow>{t("solutions-label")}</Eyebrow>
              <h2 id="solutions-title">{t("solutions-title")}</h2>
            </div>
            <Lead>{t("solutions-intro")}</Lead>
          </ServicesHeader>
          <ServiceExplorer>
            <ServiceTabs
              role="tablist"
              aria-label={t("services-explorer-label")}
              aria-orientation="vertical"
            >
              {solutions.map(({ key, icon: Icon }, index) => (
                <ServiceTab
                  key={key}
                  type="button"
                  role="tab"
                  id={`service-tab-${key}`}
                  aria-controls={`service-panel-${key}`}
                  aria-selected={activeService === index}
                  aria-label={t(`${key}-title`)}
                  tabIndex={activeService === index ? 0 : -1}
                  ref={(element) => {
                    serviceTabs.current[index] = element
                  }}
                  onClick={() => setActiveService(index)}
                  onKeyDown={(event) => handleServiceKey(event, index)}
                >
                  <span>
                    <Icon aria-hidden="true" />
                  </span>
                  <span>
                    <strong>{t(`${key}-title`)}</strong>
                    <small>{t(`service-${key}-hint`)}</small>
                  </span>
                  <FiArrowRight aria-hidden="true" />
                </ServiceTab>
              ))}
            </ServiceTabs>
            <div>
              {solutions.map(({ key, to, image, accent }, index) => (
                <ServicePanel
                  key={key}
                  role="tabpanel"
                  id={`service-panel-${key}`}
                  aria-labelledby={`service-tab-${key}`}
                  hidden={activeService !== index}
                  tabIndex={0}
                >
                  <ServiceImage
                    fluid={image.childImageSharp.fluid}
                    alt={t(`service-${key}-alt`)}
                    imgStyle={{ objectPosition: "75% center" }}
                    fadeIn={false}
                    loading="eager"
                  />
                  <ServiceMarker $accent={accent} aria-hidden="true">
                    0{index + 1} / 04
                  </ServiceMarker>
                  <ServiceContent>
                    <h3>{t(`service-${key}-headline`)}</h3>
                    <p>{t(`${key}-desc`)}</p>
                    <ul>
                      {[1, 2, 3].map((n) => (
                        <li key={n}>{t(`service-${key}-tag-${n}`)}</li>
                      ))}
                    </ul>
                    <ServiceAction to={to}>
                      {t(`${key}-link`)}
                      <FiArrowUpRight aria-hidden="true" />
                    </ServiceAction>
                  </ServiceContent>
                </ServicePanel>
              ))}
            </div>
          </ServiceExplorer>
          <ExtraServices>
            <span>{t("other-services")}</span>
            {[
              ["consulting", "/consultoria/"],
              ["education", "/capacitacion/"],
              ["blockchain", "/blockchain2/"],
              ["iot", "/iot/"],
            ].map(([key, to]) => (
              <Link key={key} to={to}>
                {t(key)}
              </Link>
            ))}
          </ExtraServices>
        </Container>
      </Section>
      <ProductSection id="productos" aria-labelledby="products-title">
        <Container>
          <ProductHeader>
            <div>
              <Eyebrow>{t("products-label")}</Eyebrow>
              <h2 id="products-title">{t("products-title")}</h2>
            </div>
            <div>
              <Lead>{t("products-intro")}</Lead>
              <CatalogLink to="/productos/">
                {t("catalog-cta")}
                <FiArrowRight aria-hidden="true" />
              </CatalogLink>
            </div>
          </ProductHeader>
          <ProductGrid>
            {products.map(
              (
                { key, name, to, icon: Icon, image, accent, position },
                index
              ) => {
                const featured = index === 0
                return (
                  <ProductTile key={key} $featured={featured} $accent={accent}>
                    <ProductEntry
                      to={to}
                      $featured={featured}
                      ariaLabel={`${t("product-link")}: ${name}`}
                    >
                      <ProductVisual $featured={featured}>
                        <Img
                          fluid={image.childImageSharp.fluid}
                          alt={t(`product-${key}-alt`)}
                          imgStyle={{ objectPosition: position }}
                        />
                        <ProductBadge>
                          <Icon aria-hidden="true" />
                          {t(`product-${key}-verb`)}
                        </ProductBadge>
                      </ProductVisual>
                      <ProductBody $featured={featured}>
                        <ProductCategory $featured={featured}>
                          {t(`product-${key}-category`)}
                        </ProductCategory>
                        <h3>{name}</h3>
                        <p>{t(`product-${key}-desc`)}</p>
                        {featured && (
                          <ProductTags>
                            {[1, 2].map((n) => (
                              <li key={n}>{t(`product-devops-tag-${n}`)}</li>
                            ))}
                          </ProductTags>
                        )}
                        <ProductAction $featured={featured}>
                          {t("product-link")}
                          <FiArrowUpRight aria-hidden="true" />
                        </ProductAction>
                      </ProductBody>
                    </ProductEntry>
                  </ProductTile>
                )
              }
            )}
          </ProductGrid>
        </Container>
      </ProductSection>
      <MethodSection id="como-trabajamos" aria-labelledby="method-title">
        <Container>
          <MethodHeader>
            <div>
              <Eyebrow>{t("method-label")}</Eyebrow>
              <h2 id="method-title">{t("method-title")}</h2>
            </div>
            <Lead>{t("method-intro")}</Lead>
          </MethodHeader>
          <MethodJourney role="list" aria-label={t("method-steps-label")}>
            {methodSteps.map(({ icon: Icon, tone }, index) => {
              const step = index + 1
              return (
                <MethodStage key={step} $tone={tone} role="listitem">
                  <MethodConnector aria-hidden="true" />
                  <MethodNumber aria-hidden="true">0{step}</MethodNumber>
                  <MethodCard>
                    <MethodPhase>
                      <Icon aria-hidden="true" />
                      {t(`step-${step}-phase`)}
                    </MethodPhase>
                    <h3>{t(`step-${step}-title`)}</h3>
                    <p>{t(`step-${step}-desc`)}</p>
                    <MethodOutcome>
                      <span>{t("method-outcome-label")}</span>
                      <strong>{t(`step-${step}-outcome`)}</strong>
                    </MethodOutcome>
                  </MethodCard>
                </MethodStage>
              )
            })}
          </MethodJourney>
          <MethodFollowThrough>
            <div>
              <FiRefreshCw aria-hidden="true" />
              <div>
                <strong>{t("method-feedback-title")}</strong>
                <p>{t("method-feedback-desc")}</p>
              </div>
            </div>
            <Link to="#contacto">
              {t("method-cta")}
              <FiArrowUpRight aria-hidden="true" />
            </Link>
          </MethodFollowThrough>
        </Container>
      </MethodSection>
      <SoftSection>
        <About>
          <div>
            <Eyebrow>{t("about-label")}</Eyebrow>
            <h2>{t("about-title")}</h2>
          </div>
          <div>
            <Lead>{t("about-desc")}</Lead>
            <TextLink to="/info/quienes-somos/">
              {t("about-cta")}
              <FiArrowRight aria-hidden="true" />
            </TextLink>
          </div>
        </About>
      </SoftSection>
      <Contact id="contacto" aria-labelledby="contact-title">
        <Container>
          <Eyebrow>{t("contact-label")}</Eyebrow>
          <h2 id="contact-title">{t("contact-title")}</h2>
          <Lead>{t("contact-desc")}</Lead>
          <Actions>
            <ButtonLink to={email}>{t("email-cta")}</ButtonLink>
          </Actions>
          <Note>
            {t("email-note")}
            <br />
            <Link to="mailto:gerencia@newinntech.com">
              gerencia@newinntech.com
            </Link>
          </Note>
        </Container>
      </Contact>
    </Page>
  )
}
export default HomePage

export const query = graphql`
  query HomePageQuery {
    serviceSoftware: file(
      relativePath: { eq: "development/software-engineering-v1.png" }
    ) {
      ...HomeServiceImage
    }
    serviceAi: file(relativePath: { eq: "ai/enterprise-ai-v1.png" }) {
      ...HomeServiceImage
    }
    serviceCloud: file(
      relativePath: { eq: "arquitectura/solution-design-v1.png" }
    ) {
      ...HomeServiceImage
    }
    serviceDevsecops: file(
      relativePath: { eq: "devsecops/secure-delivery-v1.png" }
    ) {
      ...HomeServiceImage
    }
    productDevops: file(
      relativePath: { eq: "products/devops-editorial-v1.png" }
    ) {
      ...HomeProductImage
    }
    productPmo: file(relativePath: { eq: "products/pmo-editorial-v1.png" }) {
      ...HomeProductImage
    }
    productGencode: file(
      relativePath: { eq: "products/gencode-editorial-v1.png" }
    ) {
      ...HomeProductImage
    }
    corporateHero: file(
      relativePath: { eq: "home/corporate-architecture-v1.png" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
  fragment HomeProductImage on File {
    childImageSharp {
      fluid(maxWidth: 1100, quality: 85) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }
  fragment HomeServiceImage on File {
    childImageSharp {
      fluid(maxWidth: 1100, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }
`
